import { LINK_FROM_APP_PLATFORMS, QUERY_PARAM_KEYS, REG_PATH_KEYS } from '$lib/utils/constants';
import { ApiService } from '$lib/services/api';
import { Sentry } from '$lib/services/sentry';
import { getQueryParamByKey } from '$lib/utils/browser';
import { get } from 'svelte/store';
import { app } from '$lib/stores/app/store';

const SAVINGS_FALLBACK = '%%';

type BillingPlanInfo = {
	initial_price: number;
	initial_period: number;
	initial_term: string;
	renewal_price: string;
	renewal_term: string;
	renewal_period: number;
	currency_code: string;
};

type ProductInfo = {
	productHash?: string;
	billing_plan_info?: BillingPlanInfo;
};

/**
 * @param {BillingPlanInfo} billingPlanInfo - The billing plan information
 * @returns {{ trialDays: number, trialLabel: string, hasTrial: boolean, trialTerm: string, trialPeriod: number }} The trial information
 */
export const getTrialInfo = (
	billingPlanInfo: BillingPlanInfo
): {
	trialDays: number;
	trialLabel: string;
	hasTrial: boolean;
	trialTerm: string;
	trialPeriod: number;
} => {
	const initialPrice = billingPlanInfo.initial_price;
	const initialPeriod = billingPlanInfo.initial_period;
	const initialTerm = billingPlanInfo.initial_term;

	if (initialPrice === 0) {
		const termToDays: Record<string, number> = {
			Day: 1,
			Week: 7,
			Month: 30,
			Year: 365,
		};

		const trialDays = initialPeriod * (termToDays[initialTerm] || 30);

		let trialLabel: string;
		switch (true) {
			case initialTerm === 'Month' && initialPeriod === 1:
				trialLabel = 'First Month Free';
				break;
			case initialTerm === 'Week' && initialPeriod === 1:
				trialLabel = 'First Week Free';
				break;
			case initialTerm === 'Day' && initialPeriod === 7:
				trialLabel = '7 Day Trial';
				break;
			default:
				trialLabel = `${initialPeriod} ${initialTerm}${initialPeriod > 1 ? 's' : ''} Free`;
				break;
		}

		return {
			trialDays,
			trialLabel,
			hasTrial: true,
			trialTerm: initialTerm,
			trialPeriod: initialPeriod,
		};
	}

	return { trialDays: 0, trialLabel: '', hasTrial: false, trialTerm: '', trialPeriod: 0 };
};

/**
 * @param {string} currencyCode - The currency code
 * @returns {string} The currency symbol
 */
export const getCurrencySymbol = (currencyCode: string): string => {
	return currencyCode === 'USD' ? '$' : currencyCode;
};

/**
 * Annual vs monthly: (monthly*12 - annual) / (monthly*12).
 * Returns "%%" when data is missing or invalid.
 * @param {string} [monthlyPrice] - Monthly renewal price
 * @param {string} [annualPrice] - Annual renewal price
 * @returns {number|string} Savings percentage 0–100 or "%%" fallback
 */
export const getAnnualSavingsPercentage = (
	monthlyPrice?: string,
	annualPrice?: string
): number | string => {
	if (monthlyPrice == null || annualPrice == null || monthlyPrice === '' || annualPrice === '') {
		return SAVINGS_FALLBACK;
	}
	const monthlyYearlyCost = parseFloat(monthlyPrice) ? parseFloat(monthlyPrice) * 12 : 14.99 * 12;
	const annualCost = parseFloat(annualPrice);
	if (Number.isNaN(annualCost) || monthlyYearlyCost <= 0) {
		return SAVINGS_FALLBACK;
	}
	const savingsAmount = monthlyYearlyCost - annualCost;
	return Math.floor((savingsAmount / monthlyYearlyCost) * 100);
};

/**
 * Max tier discount: (monthly*12 + assessments*12 - maxTierPrice) / (monthly*12 + assessments*12).
 * Uses API prices when available; falls back to hardcoded defaults when missing.
 * Returns "%%" when data is missing or invalid.
 * @param {string} [maxTierPrice] - Max tier price (display price)
 * @param {{ renewalPrice: string }|null} [monthlyData] - Monthly product data (for yearly cost)
 * @param {{ renewalPrice: string }|null} [assessmentsData] - Assessments product data (for yearly cost)
 * @returns {number|string} Discount percentage 0–100 or "%%" fallback
 */
export const getMaxTierDiscountPercentage = (
	maxTierPrice?: string,
	monthlyData?: { renewalPrice: string } | null,
	assessmentsData?: { renewalPrice: string } | null
): number | string => {
	if (maxTierPrice == null || maxTierPrice === '') {
		return SAVINGS_FALLBACK;
	}
	const maxTierNum = parseFloat(maxTierPrice);
	if (Number.isNaN(maxTierNum)) {
		return SAVINGS_FALLBACK;
	}
	const monthlyYearly = monthlyData ? parseFloat(monthlyData.renewalPrice) * 12 : 14.99 * 12;
	const assessmentsYearly = assessmentsData
		? parseFloat(assessmentsData.renewalPrice) * 12
		: 3.99 * 12;
	const riqOfferYearly = 7.99 * 12;
	const combined = monthlyYearly + assessmentsYearly + riqOfferYearly;
	if (combined <= 0 || Number.isNaN(combined)) {
		return SAVINGS_FALLBACK;
	}
	const discount = ((combined - maxTierNum) / combined) * 100;
	return Math.max(0, Math.floor(discount));
};

export type ExtractedProductData = {
	renewalPrice: string;
	initialPrice: string;
	currencyCode: string;
	currency: string;
	hash: string;
	trialInfo: {
		trialDays: number;
		trialLabel: string;
		hasTrial: boolean;
		trialTerm: string;
		trialPeriod: number;
	};
	paymentInterval: string;
	paymentIntervalCount: number;
};

export type ApplePayTrialMetadata = {
	trialLengthInDays: number;
	trialTerm: string;
	trialPeriod: number;
	trialLabel: string;
	paymentInterval: string;
	paymentIntervalCount: number;
};

/**
 * @param {unknown} info - Product info from API
 * @returns {boolean} True if info has productHash and billing_plan_info
 */
export const hasValidProductInfo = (info: unknown): boolean => {
	const product = info as ProductInfo | null | undefined;
	return product?.productHash != null && product?.billing_plan_info != null;
};

/**
 * Normalizes raw API product shape into a consistent structure.
 * Returns null if billing_plan_info is missing.
 * @param {unknown} productInfo - Product information from API
 * @returns {ExtractedProductData|null} Extracted product data or null if invalid
 */
export const extractProductData = (productInfo: unknown): ExtractedProductData | null => {
	const info = productInfo as ProductInfo | null | undefined;
	const billingPlanInfo = info?.billing_plan_info;
	if (!billingPlanInfo) return null;

	return {
		renewalPrice: billingPlanInfo.renewal_price,
		initialPrice: String(billingPlanInfo.initial_price),
		currencyCode: billingPlanInfo.currency_code,
		currency: getCurrencySymbol(billingPlanInfo.currency_code),
		hash: info.productHash ?? '',
		trialInfo: getTrialInfo(billingPlanInfo),
		paymentInterval: billingPlanInfo.renewal_term.toLowerCase(),
		paymentIntervalCount: billingPlanInfo.renewal_period,
	};
};

/**
 * Resolves Apple Pay trial metadata from backend product billing info.
 * @param {string} productHash - Subscription plan hash
 * @returns {Promise<ApplePayTrialMetadata|null>} Apple Pay trial metadata or null when lookup fails
 */
export const getApplePayTrialMetadata = async (
	productHash: string
): Promise<ApplePayTrialMetadata | null> => {
	try {
		const productInfo = await ApiService.getInfoForHash({
			subscription_plan_hash: productHash,
		});
		const productData = extractProductData({ ...productInfo, productHash });
		if (!productData) return null;

		return {
			trialLengthInDays: productData.trialInfo.hasTrial ? productData.trialInfo.trialDays : 0,
			trialTerm: productData.trialInfo.hasTrial ? productData.trialInfo.trialTerm : '',
			trialPeriod: productData.trialInfo.hasTrial ? productData.trialInfo.trialPeriod : 0,
			trialLabel: productData.trialInfo.hasTrial ? productData.trialInfo.trialLabel : '',
			paymentInterval: productData.paymentInterval,
			paymentIntervalCount: productData.paymentIntervalCount,
		};
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed to resolve Apple Pay trial metadata',
				productHash,
			},
		});
		return null;
	}
};

/**
 * Determines the appropriate regPathKey based on the platform stored in app state.
 * Used for iOS and Android Alt Payment flows from the inbound-gateway.
 * @param {string} defaultRegPathKey - Fallback key when platform doesn't match a known variant
 * @param {object} [options] - Options object
 * @param {boolean} [options.isResubscribe] - Use resubscriber keys instead of subscriber keys (default: false)
 * @returns {string} The appropriate REG_PATH_KEYS value
 */
export const getRegPathKey = (
	defaultRegPathKey: string,
	{ isResubscribe = false }: { isResubscribe?: boolean } = {}
): string => {
	const platform = get(app).global.linkFromAppPlatform;

	switch (platform) {
		case LINK_FROM_APP_PLATFORMS.IOS_1_0:
			return isResubscribe
				? REG_PATH_KEYS.IOS_ALT_PAYMENT_RESUBSCRIBER_1_0
				: REG_PATH_KEYS.IOS_ALT_PAYMENT_SUBSCRIBER_1_0;
		case LINK_FROM_APP_PLATFORMS.IOS_2_0:
			return isResubscribe
				? REG_PATH_KEYS.IOS_ALT_PAYMENT_RESUBSCRIBER_2_0
				: REG_PATH_KEYS.IOS_ALT_PAYMENT_SUBSCRIBER_2_0;
		case LINK_FROM_APP_PLATFORMS.ANDROID_1_0:
			return isResubscribe
				? REG_PATH_KEYS.ANDROID_ALT_PAYMENT_RESUBSCRIBER_1_0
				: REG_PATH_KEYS.ANDROID_ALT_PAYMENT_SUBSCRIBER_1_0;
		case LINK_FROM_APP_PLATFORMS.ANDROID:
		case LINK_FROM_APP_PLATFORMS.ANDROID_2_0:
		case LINK_FROM_APP_PLATFORMS.SAMSUNG:
			return isResubscribe
				? REG_PATH_KEYS.ANDROID_ALT_PAYMENT_RESUBSCRIBER_2_0
				: REG_PATH_KEYS.ANDROID_ALT_PAYMENT_SUBSCRIBER_2_0;
		default:
			return defaultRegPathKey;
	}
};

/**
 * Parses the data from the URL
 * @param {URL} url - The URL to parse the data from
 * @returns {{ productHashMonthly: string | null, productHashAnnual: string | null, productHashMaxTier: string | null, productHashAssessments: string | null }} - The parsed data with product hashes (assessments is optional, used only for discount %)
 */
export function checkForInboundGatewayData(url: URL): {
	productHashMonthly: string | null;
	productHashAnnual: string | null;
	productHashMaxTier: string | null;
	productHashAssessments: string | null;
} {
	let productHashMonthly = null;
	let productHashAnnual = null;
	let productHashMaxTier = null;
	let productHashAssessments = null;
	const data = getQueryParamByKey(url, QUERY_PARAM_KEYS.PAYLOAD);
	if (!data) {
		return { productHashMonthly, productHashAnnual, productHashMaxTier, productHashAssessments };
	}

	try {
		const parsedData = JSON.parse(data);
		const products = parsedData.data?.products || parsedData.products || [];

		for (const product of products) {
			if (product.type === 'monthly') {
				productHashMonthly = product.hash;
			} else if (product.type === 'annual') {
				productHashAnnual = product.hash;
			} else if (product.type === 'max-tier') {
				productHashMaxTier = product.hash;
			} else if (product.type === 'assessments') {
				productHashAssessments = product.hash;
			}
		}

		return { productHashMonthly, productHashAnnual, productHashMaxTier, productHashAssessments };
	} catch (error) {
		Sentry.captureException(error, {
			extra: { action: 'Error: Failed to parse inbound gateway data' },
		});
		return {
			productHashMonthly: null,
			productHashAnnual: null,
			productHashMaxTier: null,
			productHashAssessments: null,
		};
	}
}

/**
 * Get product info.
 * @param {object} payload payload
 * @param {string | null} payload.productHashMonthly product hash monthly
 * @param {string | null} payload.productHashAnnual product hash annual
 * @param {string | null} [payload.productHashMaxTier] product hash max tier (optional)
 * @param {string | null} [payload.productHashAssessments] product hash assessments (optional; used only for discount %)
 * @returns {Promise<{ monthlyProductInfo: object, annualProductInfo: object, maxTierProductInfo: object, assessmentsProductInfo: object }>}
 */
export async function getProductInfo({
	productHashMonthly,
	productHashAnnual,
	productHashMaxTier = null,
	productHashAssessments = null,
}: {
	productHashMonthly: string | null;
	productHashAnnual: string | null;
	productHashMaxTier?: string | null;
	productHashAssessments?: string | null;
}): Promise<{
	monthlyProductInfo: object;
	annualProductInfo: object;
	maxTierProductInfo: object;
	assessmentsProductInfo: object;
}> {
	let monthlyProductInfo = {};
	let annualProductInfo = {};
	let maxTierProductInfo = {};
	let assessmentsProductInfo = {};

	/**
	 * @param {string} hash - Subscription plan hash.
	 * @param {string} key - Key for error reporting.
	 * @returns {Promise<object>} - The product info
	 */
	const fetchHash = async (hash: string, key: string): Promise<object> => {
		try {
			return await ApiService.getInfoForHash({ subscription_plan_hash: hash });
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: `Error: Invalid Subscription Plan Hash for getProductInfo (${key})`,
					[key]: hash,
				},
			});
			return {};
		}
	};

	if (productHashMonthly) {
		monthlyProductInfo = await fetchHash(productHashMonthly, 'productHashMonthly');
		if (Object.keys(monthlyProductInfo).length > 0) {
			monthlyProductInfo = { ...monthlyProductInfo, productHash: productHashMonthly };
		}
	}
	if (productHashAnnual) {
		annualProductInfo = await fetchHash(productHashAnnual, 'productHashAnnual');
		if (Object.keys(annualProductInfo).length > 0) {
			annualProductInfo = { ...annualProductInfo, productHash: productHashAnnual };
		}
	}
	if (productHashMaxTier) {
		maxTierProductInfo = await fetchHash(productHashMaxTier, 'productHashMaxTier');
		if (Object.keys(maxTierProductInfo).length > 0) {
			maxTierProductInfo = { ...maxTierProductInfo, productHash: productHashMaxTier };
		}
	}
	if (productHashAssessments) {
		assessmentsProductInfo = await fetchHash(productHashAssessments, 'productHashAssessments');
		if (Object.keys(assessmentsProductInfo).length > 0) {
			assessmentsProductInfo = { ...assessmentsProductInfo, productHash: productHashAssessments };
		}
	}

	return {
		monthlyProductInfo,
		annualProductInfo,
		maxTierProductInfo,
		assessmentsProductInfo,
	};
}
